import React, { useCallback, useState } from 'react';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { workItemsApi } from '../../api/services';
import { useEntitlements } from '../../context/EntitlementsContext';
import type { WorkItem } from '../../types/models';
import { composeDecisionTitle } from './composeDecisionTitle';

/** YYYY-MM-DD from LOCAL date components (never toISOString, which is UTC). */
function localDateOnly(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Decision outcome capture, shared by every surface that can complete a work
 * item.
 *
 * This is a hook rather than page-local state because completing an item
 * happens in at least five places (Work Items, Dashboard ×2, the briefing
 * dashboard, and the focus session cards). Wiring the prompt into only one of
 * them means decisions ticked off the main daily surface are never captured —
 * which is most of them.
 *
 * Usage:
 *   const { completeWorkItem, dialog } = useDecisionCapture(reload);
 *   ...
 *   onClick={() => completeWorkItem(item)}
 *   ...
 *   {dialog}
 *
 * See shared-docs/DECISION-CAPTURE-SPEC.md.
 *
 * The decision log is its own entitlement: on a plan without it, a Decision
 * item completes like any other (no prompt, no Decision record).
 */
export function useDecisionCapture(
  onCompleted: () => void | Promise<void>,
  onError?: (message: string) => void,
) {
  const decisionsIncluded = useEntitlements().has('decisions');
  const [prompt, setPrompt] = useState<{
    item: WorkItem;
    outcome: string;
    saving: boolean;
  } | null>(null);

  const completePlain = useCallback(async (id: string) => {
    try {
      await workItemsApi.update(id, { status: 'Done' });
      await onCompleted();
    } catch (err: any) {
      onError?.(err?.response?.data?.error ?? 'Failed to mark work item complete');
    }
  }, [onCompleted, onError]);

  /**
   * Complete an item. Decision items get the capture prompt first; everything
   * else completes immediately, exactly as before.
   */
  const completeWorkItem = useCallback(async (item: WorkItem) => {
    if (item.type === 'Decision' && decisionsIncluded) {
      setPrompt({ item, outcome: '', saving: false });
      return;
    }
    await completePlain(item.id);
  }, [completePlain, decisionsIncluded]);

  const save = useCallback(async () => {
    // Guard inside save(), not just on the button's disabled state: Cmd+Enter
    // bypasses the button entirely, and a double-tap would fire two POSTs that
    // each create a Decision — leaving one orphaned, the exact state
    // RecompletingAfterReopen_...DoesNotOrphanASecond exists to prevent.
    if (!prompt || prompt.saving) return;
    const outcome = prompt.outcome.trim();
    if (!outcome) return;
    setPrompt({ ...prompt, saving: true });
    try {
      await workItemsApi.completeWithDecision(prompt.item.id, {
        outcome,
        // Send the local date so an evening decision isn't logged as tomorrow.
        decisionDate: localDateOnly(new Date()),
      });
      setPrompt(null);
      await onCompleted();
    } catch (err: any) {
      setPrompt(p => (p ? { ...p, saving: false } : p));
      onError?.(err?.response?.data?.error ?? 'Failed to record the decision');
    }
  }, [prompt, onCompleted, onError]);

  // Skip = "not now". The item completes and reappears in Close Day so the
  // outcome can still be captured while recall is good.
  const skip = useCallback(async () => {
    if (!prompt || prompt.saving) return;
    const id = prompt.item.id;
    setPrompt(null);
    await completePlain(id);
  }, [prompt, completePlain]);

  // "No record needed" = settled. Completes and never asks again. Kept
  // deliberately distinct from Skip: without it the backstop nags forever about
  // decisions the user has already judged not worth logging, and a backstop
  // people learn to ignore protects nothing.
  const declineRecord = useCallback(async () => {
    if (!prompt || prompt.saving) return;
    const id = prompt.item.id;
    setPrompt(null);
    try {
      await workItemsApi.declineDecisionRecord(id);
      await onCompleted();
    } catch (err: any) {
      onError?.(err?.response?.data?.error ?? 'Failed to complete the work item');
    }
  }, [prompt, onCompleted, onError]);

  const dialog = (
    /* Dismissing (Esc, overlay, X) CANCELS — it does not complete. Nothing has
       been written at this point, so there's no half-ticked row to resolve, and
       treating dismissal as Skip would leave a misclick on the ✓ with no way
       back. Completing is only ever an explicit choice. */
    <Dialog
      open={prompt !== null}
      onOpenChange={(o) => { if (!o && prompt && !prompt.saving) setPrompt(null); }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>What was decided?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{prompt?.item.title}</p>
          <Textarea
            autoFocus
            rows={3}
            placeholder="e.g. Went with vendor B — better support terms"
            value={prompt?.outcome ?? ''}
            maxLength={500}
            onChange={(e) => setPrompt(p => (p ? { ...p, outcome: e.target.value } : p))}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save(); }}
          />
          {/* Show the composed title. The decision log records the question as
              well as the answer, so a one-word outcome still reads sensibly
              months later — and the user can see that before they commit. */}
          <p className="text-xs text-muted-foreground">
            Saved to your decision log as{' '}
            <span className="font-medium text-foreground">
              {composeDecisionTitle(prompt?.item.title ?? '', prompt?.outcome ?? '')}
            </span>
          </p>
        </div>
        <DialogFooter className="sm:justify-between">
          {/* Tertiary and visually quiet — it's the rarer, more final choice,
              and shouldn't compete with recording an outcome. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={declineRecord}
            disabled={prompt?.saving}
            className="text-muted-foreground"
          >
            No record needed
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={skip} disabled={prompt?.saving}>Skip for now</Button>
            <Button onClick={save} disabled={!prompt?.outcome.trim() || prompt?.saving}>
              {prompt?.saving ? 'Saving…' : 'Save & complete'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { completeWorkItem, dialog };
}
